import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { CareArea } from '../../interfaces/care-area';
import { Drug } from '../../interfaces/drug';
import { DrugConcentration } from '../../interfaces/drug-concentration';
import { Facility } from '../../interfaces/facility';
import { RootState } from 'store';

interface DeviceState {
  selectedFacility: null | Facility;
  selectedCareArea: null | CareArea;
  selectedDrug: null | Drug;
  selectedDrugConcentration: null | DrugConcentration;
  isAddingCareAreas: boolean;
  isAddingConcentration: boolean;
  expandedDrugs: number[]; // Drug ids
  expandedThrapies: number[]; // List Concentration ids
  isEditingStill: boolean;
  drugUnsavedChangesMessage: string;
  blockDoubleModal: boolean;
}

const initialState: DeviceState = {
  selectedFacility: null,
  selectedCareArea: null,
  selectedDrug: null,
  selectedDrugConcentration: null,
  isAddingCareAreas: false,
  isAddingConcentration: false,
  expandedDrugs: [],
  expandedThrapies: [],
  isEditingStill: false,
  drugUnsavedChangesMessage: '',
  blockDoubleModal: false,
};

const deviceSlice = createSlice({
  name: 'device',
  initialState,
  reducers: {
    updateSelectedFacility: (state, action) => {
      state.selectedFacility = action.payload.facility;
    },
    updateSelectedCareArea: (state, action) => {
      state.selectedCareArea = action.payload.careArea;
    },
    updateIsEditingStill: (
      state,
      action: PayloadAction<
        Pick<DeviceState, 'isEditingStill' | 'drugUnsavedChangesMessage'>
      >,
    ) => {
      state.isEditingStill = action.payload.isEditingStill;
      state.drugUnsavedChangesMessage =
        action.payload.drugUnsavedChangesMessage;
    },
    updateBlockDoubleModal: (
      state,
      action: PayloadAction<DeviceState['blockDoubleModal']>,
    ) => {
      state.blockDoubleModal = action.payload;
    },
    updateSelectedDrug: (state, action) => {
      state.selectedDrug = action.payload.drug;
      state.selectedDrugConcentration = null;
    },
    updateSelectedDrugConcentration: (state, action) => {
      state.selectedDrugConcentration = action.payload.concentration;
      state.selectedDrug = null;
    },
    clearSelectedFacility: (state) => {
      state.selectedFacility = null;
    },
    clearSelectedCareArea: (state) => {
      state.selectedCareArea = null;
    },
    clearSelectedDrugConcentration: (state) => {
      state.selectedDrugConcentration = null;
    },
    clearSelectedDrug: (state) => {
      state.selectedDrug = null;
    },
    setAddingCareArea: (state) => {
      state.isAddingCareAreas = true;
      state.isAddingConcentration = false;
    },
    clearAddingCareArea: (state) => {
      state.isAddingCareAreas = false;
    },
    setAddingConcentration: (state) => {
      state.isAddingConcentration = true;
      state.isAddingCareAreas = false;
    },
    clearAddingConcentration: (state) => {
      state.isAddingConcentration = false;
    },
    reset: (state) => {
      state.selectedFacility = null;
      state.selectedCareArea = null;
      state.selectedDrugConcentration = null;
      state.isAddingCareAreas = false;
      state.isAddingConcentration = false;
    },
    setExpandedDrug: (state, action) => {
      const id = Number(action.payload.id);

      state.expandedDrugs = [id];
    },
    updateExpandedDrugs: (state, action) => {
      const id = Number(action.payload.id);
      if (state.expandedDrugs.includes(id)) {
        state.expandedDrugs = state.expandedDrugs.filter(
          (drugId: number) => id !== drugId,
        );
      } else {
        state.expandedDrugs.push(id);
      }
    },
    clearExpandedDrugs: (state) => {
      state.expandedDrugs = [];
    },
    setExpandedTherapies: (state, action) => {
      const id = Number(action.payload.id);

      state.expandedThrapies = [id];
    },
    updateExpandedTherapies: (state, action) => {
      const id = Number(action.payload.id);
      if (state.expandedThrapies.includes(id)) {
        state.expandedThrapies = state.expandedThrapies.filter(
          (drugId: number) => id !== drugId,
        );
      } else {
        state.expandedThrapies.push(id);
      }
    },
    clearExpandedTherapies: (state) => {
      state.expandedThrapies = [];
    },
  },
});

/**
 * @description Redux selector that retrieves the currently selected drug concentration from device state
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {RootState} state - Redux root state
 *
 * @returns {DrugConcentration | null} Selected drug concentration or null
 */
export const selectSelectedDrugConcentration = (
  state: RootState,
): DrugConcentration | null => state.device.selectedDrugConcentration;
/**
 * @description Redux selector that retrieves the currently selected facility from device state
 *
 * @SADtrace SAD-003-4.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {RootState} state - Redux root state
 *
 * @returns {Facility | null} Selected facility or null
 */
export const selectSelectedFacility = (state: RootState): Facility | null =>
  state.device.selectedFacility;

/**
 * @description Redux selector that retrieves the currently selected care area from device state
 *
 * @SADtrace SAD-003-4.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {RootState} state - Redux root state
 *
 * @returns {CareArea | null} Selected care area or null
 */
export const selectSelectedCareArea = (state: RootState): CareArea | null =>
  state.device.selectedCareArea;
/**
 * @description Redux selector that retrieves the currently selected drug from device state
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {RootState} state - Redux root state
 *
 * @returns {Drug | null} Selected drug or null
 */
export const selectSelectedDrug = (state: RootState): Drug | null =>
  state.device.selectedDrug;
/**
 * @description Redux selector that retrieves whether the user is currently adding care areas
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.17
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {RootState} state - Redux root state
 *
 * @returns {boolean} True if adding care areas, false otherwise
 */
export const selectIsAddingCareAreas = (state: RootState): boolean =>
  state.device.isAddingCareAreas;

/**
 * @description Redux selector that retrieves whether the user is currently adding a concentration
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2, SAD-003-4.17
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {RootState} state - Redux root state
 *
 * @returns {boolean} True if adding concentration, false otherwise
 */
export const selectIsAddingConcentration = (state: RootState): boolean =>
  state.device.isAddingConcentration;

/**
 * @description Redux selector that retrieves the list of expanded drug IDs in the UI
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {RootState} state - Redux root state
 *
 * @returns {number[]} Array of expanded drug IDs
 */
export const selectExpandedDrugs = (state: RootState): number[] =>
  state.device.expandedDrugs;

/**
 * @description Redux selector that retrieves the list of expanded therapy IDs in the UI
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {RootState} state - Redux root state
 *
 * @returns {number[]} Array of expanded therapy IDs
 */
export const selectExpandedTherapies = (state: RootState): number[] =>
  state.device.expandedThrapies;

/**
 * @description Redux selector that retrieves whether there are unsaved changes being edited
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {RootState} state - Redux root state
 *
 * @returns {boolean} True if user is still editing with unsaved changes, false otherwise
 */
export const selectIsEditingStill = (state: RootState): boolean =>
  state.device.isEditingStill;

/**
 * @description Redux selector that retrieves the custom message for unsaved drug changes warning
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {RootState} state - Redux root state
 *
 * @returns {string} Custom unsaved changes message for drug editing
 */
export const selectDrugUnsavedChangesMessage = (state: RootState): string =>
  state.device.drugUnsavedChangesMessage;

export const deviceActions = deviceSlice.actions;

export default deviceSlice.reducer;
